import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { addCouponAPI } from "../../utils/api";
import Sidebar from "../Sidebar/Sidebar";
import "./AddCoupon.css";
import Toast from "../../components/Toast/Toast";

const AddCoupon = () => {
  const navigate = useNavigate();
  const [couponData, setCouponData] = useState({
    code: "",
    description: "",
    discount_type: "flat",
    discount_amount: "",
    discount_percentage: "",
    expiry_date: "",
    min_order_amount: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === 'code' ? value.toUpperCase() : value;
    setCouponData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        ...couponData,
        discount_amount: couponData.discount_type === "flat" ? couponData.discount_amount : null,
        discount_percentage: couponData.discount_type === "percentage" ? couponData.discount_percentage : null,
        expiry_date: formatDate(couponData.expiry_date),
        min_order_amount: couponData.min_order_amount,
      };
      const response = await addCouponAPI(payload);

      if (response?.status === 200) {
        showToast("Coupon created successfully.", "success")
        setTimeout(() => {
          navigate("/coupons");
        }, 1000);
      } else {
        showToast(`${typeof response?.message === 'string' ? response?.message : "Failed to create coupon"}`, "error")
      }
    } catch (err) {
      showToast("Failed to create coupon", "error")
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/coupons");
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="add-coupon-layout">
        <Sidebar />
        <div className="add-coupon-main-content">
          <div className="order-details-header">
            <button className="back-button" onClick={handleCancel}>
              <FaArrowLeft /> Back to Coupons
            </button>
          </div>

          <div className="add-coupon-form-container">
            <form onSubmit={handleSubmit} className="add-coupon-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="code">Coupon Code *</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={couponData.code}
                  onChange={handleChange}
                  required
                  placeholder="Enter coupon code"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={couponData.description}
                  onChange={handleChange}
                  placeholder="Enter coupon description"
                  rows="3"
                  maxLength="2000"
                />
                <div className="character-counter">
                  {couponData.description.length}/2000 characters
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="discount_type">Discount Type *</label>
                <select
                  id="discount_type"
                  name="discount_type"
                  value={couponData.discount_type}
                  onChange={handleChange}
                  required
                >
                  <option value="flat">Flat Amount</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>

              {couponData.discount_type === "flat" ? (
                <div className="form-group">
                  <label htmlFor="discount_amount">Discount Amount *</label>
                  <input
                    type="text"
                    id="discount_amount"
                    name="discount_amount"
                    value={couponData.discount_amount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleChange({ target: { name: 'discount_amount', value } });
                    }}
                    required
                    placeholder="Enter discount amount"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="discount_percentage">Discount Percentage (%) *</label>
                  <input
                    type="text"
                    id="discount_percentage"
                    name="discount_percentage"
                    value={couponData.discount_percentage}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9]/g, '');
                      if (value.length > 3) value = value.slice(0, 3);
                      if (parseInt(value) > 100) value = '100';
                      handleChange({ target: { name: 'discount_percentage', value } });
                    }}
                    required
                    placeholder="Enter discount percentage"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="min_order_amount">Minimum Order Amount</label>
                <input
                  type="text"
                  id="min_order_amount"
                  name="min_order_amount"
                  value={couponData.min_order_amount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleChange({ target: { name: 'min_order_amount', value } });
                  }}
                  placeholder="Enter minimum order amount"
                />
              </div>

              <div className="form-group">
                <label htmlFor="expiry_date">Expiry Date</label>
                <input
                  type="date"
                  id="expiry_date"
                  name="expiry_date"
                  value={couponData.expiry_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="submit-btn-row">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCoupon;
